import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Save, ArrowUp, ArrowDown, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];
const COMMUNITIES = [
  { key: 'nuts', label: '🥜 Nuts' },
  { key: 'caps', label: '🧢 Caps' },
  { key: 'shark', label: '🦈 Shark' },
];
const EMPTY_ML = { ca: '', es: '', en: '', fr: '' };

const MLField = ({ label, value, onChange, multiline = false, testidBase, placeholder }) => {
  const [lang, setLang] = useState('ca');
  const v = value || EMPTY_ML;
  const InputComp = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                lang === l.code ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {l.code}
            </button>
          ))}
        </div>
      </div>
      <InputComp
        value={v[lang] || ''}
        onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
        placeholder={placeholder}
        className={multiline ? 'min-h-[140px]' : ''}
        data-testid={`help-${testidBase}-${lang}`}
      />
      {multiline && (
        <p className="text-[10px] text-slate-500">
          Format: paràgrafs separats per línia en blanc · **negreta** funciona
        </p>
      )}
    </div>
  );
};

export const CommunityHelpForm = () => {
  const [community, setCommunity] = useState('nuts');
  const [help, setHelp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async (com) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/${com}/help`);
      setHelp(r.data);
    } catch {
      toast.error('Error cargando ayuda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(community); }, [community]);

  const update = (field, value) => setHelp((h) => ({ ...h, [field]: value }));
  const updateSection = (idx, field, value) => {
    setHelp((h) => {
      const sections = [...(h.sections || [])];
      sections[idx] = { ...sections[idx], [field]: value };
      return { ...h, sections };
    });
  };
  const move = (idx, dir) => {
    setHelp((h) => {
      const arr = [...(h.sections || [])];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return h;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      arr.forEach((it, i) => (it.order = i + 1));
      return { ...h, sections: arr };
    });
  };
  const removeSection = (idx) => {
    if (!window.confirm('¿Eliminar esta sección?')) return;
    setHelp((h) => {
      const arr = [...(h.sections || [])];
      arr.splice(idx, 1);
      return { ...h, sections: arr };
    });
  };
  const addSection = () => {
    setHelp((h) => ({
      ...h,
      sections: [
        ...(h.sections || []),
        {
          key: `new-${Date.now()}`,
          icon: '✨',
          order: (h.sections?.length || 0) + 1,
          title: { ca: '', es: '', en: '', fr: '' },
          body: { ca: '', es: '', en: '', fr: '' },
          active: true,
        },
      ],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/${community}/help`, help, { headers });
      toast.success(`Ayuda de ${community} guardada`);
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !help) return <div className="p-6 text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="community-help-admin-form">
      <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-3 -mt-3 border-b">
        <div>
          <h2 className="text-2xl font-bold">Ayuda comunitats</h2>
          <p className="text-sm text-slate-500">Editable por comunidad — accesible en /[comunidad]/ajuda</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {COMMUNITIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCommunity(c.key)}
                data-testid={`help-tab-${c.key}`}
                className={`px-3 py-1.5 text-xs rounded ${
                  community === c.key ? 'bg-white shadow text-emerald-700 font-medium' : 'text-slate-600'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="help-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Encabezado</CardTitle>
          <CardDescription>Título y bienvenida</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título" testidBase="intro-title" value={help.intro_title} onChange={(v) => update('intro_title', v)} />
          <MLField label="Bienvenida" testidBase="intro-body" value={help.intro_body} onChange={(v) => update('intro_body', v)} multiline />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Secciones</CardTitle>
            <CardDescription>{help.sections?.length || 0} secciones</CardDescription>
          </div>
          <Button size="sm" onClick={addSection} variant="outline" data-testid="help-add-section">
            <Plus className="w-3 h-3 mr-1" /> Añadir
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {(help.sections || []).map((s, idx) => (
            <div key={s.key + idx} className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium text-slate-800">
                  <span className="mr-2">{s.icon}</span>
                  {s.title?.ca || s.key}
                  <span className="text-xs text-slate-500 ml-2">({s.key})</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => move(idx, -1)}><ArrowUp className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => move(idx, 1)}><ArrowDown className="w-3 h-3" /></Button>
                  <label className="flex items-center gap-1 text-xs px-2">
                    <input type="checkbox" checked={s.active !== false} onChange={(e) => updateSection(idx, 'active', e.target.checked)} />
                    Activo
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeSection(idx)} className="text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs">Icono (emoji)</Label>
                  <Input value={s.icon || ''} onChange={(e) => updateSection(idx, 'icon', e.target.value)} maxLength={4} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Key (sin espacios, único)</Label>
                  <Input value={s.key || ''} onChange={(e) => updateSection(idx, 'key', e.target.value)} />
                </div>
              </div>
              <div className="space-y-3">
                <MLField label="Título sección" testidBase={`sec-${idx}-title`} value={s.title} onChange={(v) => updateSection(idx, 'title', v)} />
                <MLField label="Cuerpo" testidBase={`sec-${idx}-body`} value={s.body} onChange={(v) => updateSection(idx, 'body', v)} multiline />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="sticky bottom-0 bg-white py-3 border-t flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando…' : 'Guardar todo'}
        </Button>
      </div>
    </div>
  );
};

export default CommunityHelpForm;
